// dd-charts.jsx — Deep Dive visualizations (V2). Ported from the Claude Design
// source `dd-charts.jsx`; `window.*` globals replaced with ES imports. Coloring runs
// through the neon color system (`ddBandColor` / `ddScoreColor`) with a `system` arg.
import React from "react";
import { TYPE_COLOR } from "./data";
import { ddBandColor, ddScoreColor } from "./dd-data";

// ---- Band ribbon: segmented bar of all bands with a marker at the group's score ----
export function BandRibbon({ bands, score, band, higherWorse, system }) {
  const n = bands.length;
  const markerPct = Math.max(0, Math.min(100, score));
  return (
    <div className="dd-ribbon">
      <div className="dd-ribbon__bar">
        {bands.map((b, i) => {
          const active = b.label === band;
          return (
            <div key={b.label}
              className={"dd-ribbon__seg" + (active ? " is-active" : "")}
              style={{ width: (b.hi - b.lo) + "%", background: b.color || ddBandColor(i, n, higherWorse, system) }}
              title={b.label}>
              {active ? <span className="dd-ribbon__seglbl">{b.label}</span> : null}
            </div>
          );
        })}
        <div className="dd-ribbon__marker" style={{ left: markerPct + "%" }}>
          <span className="dd-ribbon__val">{score}</span>
          <span className="dd-ribbon__caret"></span>
          <span className="dd-ribbon__pin"></span>
        </div>
      </div>
      <div className="dd-ribbon__axis">
        <span>0</span>
        <span className="dd-ribbon__dir">{higherWorse ? "higher = more severe →" : "higher = healthier →"}</span>
        <span>100</span>
      </div>
    </div>
  );
}

// ---- Population band distribution: single stacked horizontal bar ----
export function StackedDist({ dist, bands, higherWorse, system }) {
  const colorByLabel = {};
  bands.forEach((b, i) => { colorByLabel[b.label] = b.color || ddBandColor(i, bands.length, higherWorse, system); });
  // Fallback so a dist label that isn't in `bands` still gets a distinct color
  // (by position on the ramp) rather than rendering as a colorless segment.
  const segColor = (d, i) => colorByLabel[d.label] || ddBandColor(i, dist.length, higherWorse, system);
  return (
    <div className="dd-stack">
      <div className="dd-stack__bar">
        {dist.map((d, i) => (
          <div key={d.label} className="dd-stack__seg"
            // Sized by flex-grow rather than width% so the segments always fill the
            // bar exactly. Band percentages are rounded independently server-side and
            // can total 99 or 101; with width% that left a gap at the rounded end,
            // which showed up as a squared-off corner.
            style={{ flexGrow: d.pct, background: segColor(d, i) }}
            title={d.label + " — " + d.pct + "%"}>
            {d.pct >= 10 ? <span className="dd-stack__inpct">{d.pct}%</span> : null}
          </div>
        ))}
      </div>
      <div className="dd-stack__legend">
        {dist.map((d, i) => (
          <div key={d.label} className="dd-stack__item">
            <span className="dd-stack__sw" style={{ background: segColor(d, i) }}></span>
            <span className="dd-stack__lbl">{d.label}</span>
            <b>{d.pct}%</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Dimension breakdown: horizontal bars sorted weakest → strongest ----
export function DimBars({ dims, note, system }) {
  const sorted = [...dims].sort((a, b) => a.score - b.score);
  return (
    <div className="dd-dims">
      {sorted.map((d, i) => {
        const color = ddScoreColor(d.score, system);
        const inside = d.score >= 16; // enough fill to hold the number
        return (
          <div key={d.label} className={"dd-dim" + (i === 0 ? " is-weakest" : "")}>
            <span className="dd-dim__lbl">{d.label}{i === 0 ? <em> · weakest</em> : null}</span>
            <div className="dd-dim__barwrap">
              <span className="dd-dim__axis dd-dim__axis--min">0</span>
              <div className="dd-dim__track">
                <div className="dd-dim__fill" style={{ width: d.score + "%", background: color }}>
                  {inside ? <span className="dd-dim__num" style={{ color }}>{d.score}</span> : null}
                </div>
                {!inside ? <span className="dd-dim__num dd-dim__num--out" style={{ left: d.score + "%", color }}>{d.score}</span> : null}
              </div>
              <span className="dd-dim__axis dd-dim__axis--max">100</span>
            </div>
          </div>
        );
      })}
      {note ? <p className="dd-dims__note">{note}</p> : null}
    </div>
  );
}

// ---- Vasana type composition table (V2 replaces the donut) ----
// Fed by the live `mix` (Vasana marker distribution). The response / need columns are
// descriptive metadata for each of the four types.
export function TypeDonut({ mix }) {
  const ORDER = ["SA", "RE", "GA", "MA"];
  const META = {
    SA: { response: "Fight", bioNeed: "Dictate", psychNeed: "Certainty" },
    RE: { response: "Flight", bioNeed: "Migrate", psychNeed: "Freedom" },
    GA: { response: "Freeze", bioNeed: "Tolerate", psychNeed: "Stability" },
    MA: { response: "Fawn", bioNeed: "Hibernate", psychNeed: "Security" },
  };
  const byType = Object.fromEntries(mix.map((m) => [m.type, m.pct]));
  return (
    <div className="dd-typetable" role="table" aria-label="Vasana type composition">
      <div className="dd-typetable__head" role="row">
        <span role="columnheader">Type</span>
        <span role="columnheader">Biological response</span>
        <span role="columnheader">Biological need</span>
        <span role="columnheader">Psychological need</span>
        <span role="columnheader">Composition</span>
      </div>
      {ORDER.map((t) => {
        const nm = t[0] + t[1].toLowerCase();
        const pct = byType[t] || 0;
        const color = TYPE_COLOR[t];
        return (
          <div className="dd-typetable__row" role="row" key={t}>
            <span className="dd-typetable__type" role="cell">
              <span className="dd-typetable__dot" style={{ background: color }}></span>
              <span className="dd-typetable__name" style={{ color }}>{nm}</span>
            </span>
            <span className="dd-typetable__attr" role="cell" data-label="Biological response">{META[t].response}</span>
            <span className="dd-typetable__attr" role="cell" data-label="Biological need">{META[t].bioNeed}</span>
            <span className="dd-typetable__attr" role="cell" data-label="Psychological need">{META[t].psychNeed}</span>
            <span className="dd-typetable__comp" role="cell">
              <span className="dd-typetable__track">
                <span className="dd-typetable__bar" style={{ width: pct + "%", background: color }}></span>
              </span>
              <b style={{ color }}>{pct}%</b>
            </span>
          </div>
        );
      })}
    </div>
  );
}
